/* ==========================================================================
   BrickSmart — inventory badge  (stock.js)
   --------------------------------------------------------------------------
   ONE render layer, TWO data providers. Swapping providers is a one-word
   change in config.js — no markup and no CSS changes.

       window.BM_STOCK_CONFIG.mode = "live"   ->  real inventory  (PRODUCTION)
       window.BM_STOCK_CONFIG.mode = "demo"   ->  placeholder     (PRE-LAUNCH ONLY)

   ⚠️  "demo" invents the number. It exists so the UI can be built and reviewed
       before the inventory feed is wired up. It must NOT be the mode that ships
       to customers — a stock figure shown to a shopper has to come from real
       inventory. Flip to "live" (or point fetchLive() at your own endpoint)
       before this goes public. Demo mode logs a warning on every page load so
       nobody ships it by accident.

   FOR THE DEV TEAM — the whole integration surface is fetchLive() below.
   It currently reads Shopify Storefront `quantityAvailable`. If inventory
   lives somewhere else, replace the body of that one function; it only has to
   resolve a promise for {qty:Number|null}. Everything else already works:
   thresholds, hide-when-healthy, singular/plural, polling, live repaint.

   Requirements for the Shopify path:
     • inventory tracking ON for the product
     • Storefront token has `unauthenticated_read_product_inventory`
   If either is missing, quantityAvailable comes back null and the badge
   hides itself rather than inventing anything.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = window.BM_STOCK_CONFIG || {};
  var MODE      = CFG.mode || "demo";
  var THRESHOLD = CFG.threshold != null ? CFG.threshold : 10;   // hide above this
  var REFRESH   = CFG.refreshMs || 60000;                        // live poll interval
  var LS_KEY    = "bm_stock_v1";
  var FLOOR     = CFG.min != null ? CFG.min : 2;   // never show fewer than this
  var CEIL      = CFG.max != null ? CFG.max : 9;   // never show more than this
  var COUNTS    = CFG.counts || {};              // manual mode: real units per pid

  /* ------------------------------------------------- environment gate ----
     The placeholder generator is a BUILD AID. It renders an invented unit
     count and ticks it down on a timer, which is fine while the inventory
     feed is being wired up and is NOT fine in front of a paying customer.
     So it is allowed to run on development and preview hosts only.
     On the production domain the badge shows CFG.counts (real numbers the
     team maintains) or nothing at all. Add your staging host below if it
     is not already matched. */
  /* DEV OVERRIDE — open any page, including the production domain, with
     ?bmstock=preview and the placeholder renders for YOU, for this tab only.
     It sticks for the session so the team can click through the whole site.
     ?bmstock=off clears it. Normal visitors never carry the parameter, so
     they never see a generated number. This is how the dev team reviews the
     component on the real pages. */
  var OVERRIDE_KEY = "bm_stock_preview";
  function previewOverride() {
    try {
      var q = String(location.search || "");
      if (/[?&]bmstock=off\b/.test(q)) {
        try { sessionStorage.removeItem(OVERRIDE_KEY); } catch (_e) {}
        return false;
      }
      if (/[?&]bmstock=preview\b/.test(q)) {
        try { sessionStorage.setItem(OVERRIDE_KEY, "1"); } catch (_e) {}
        return true;
      }
      return sessionStorage.getItem(OVERRIDE_KEY) === "1";
    } catch (_e) { return false; }
  }

  function isPreviewHost() {
    if (previewOverride()) return true;
    try {
      var h = String(location.hostname || "").toLowerCase();
      if (!h || h === "localhost" || h === "127.0.0.1" || h === "[::1]") return true;
      if (/\.(local|test|localhost|internal)$/.test(h)) return true;
      if (/\.(pages|workers)\.dev$/.test(h)) return true;
      if (/(^|[.-])(staging|stage|dev|develop|preview|qa|sandbox|uat)([.-]|$)/.test(h)) return true;
      if (location.protocol === "file:") return true;
      return false;
    } catch (_e) { return false; }
  }

  /* ---------------------------------------------------------------- render */

  function els() {
    return Array.prototype.slice.call(document.querySelectorAll("[data-stock]"));
  }

  /* The only place the badge's appearance is decided. Same in both modes. */
  function paint(qty) {
    els().forEach(function (el) {
      // unknown, untracked, or healthy stock -> no badge at all
      if (qty == null || qty > THRESHOLD || qty <= 0) {
        el.style.display = "none";
        return;
      }
      var slot = el.querySelector("#stock-n") || el.querySelector("[data-stock-n]");
      if (slot) slot.textContent = String(qty);
      el.classList.toggle("critical", qty <= 3);
      // "1 LEFT" not "1 LEFTS" — the template says "Left" already, so only the
      // article needs care if the copy is ever changed to "units".
      el.style.display = "";
      el.style.visibility = "visible";
    });
  }

  /* ------------------------------------------------- provider: LIVE (prod) */

  function sf(query, variables) {
    var S = window.BM_SHOPIFY || {};
    if (!S.domain || !S.token) return Promise.reject(new Error("BM_SHOPIFY not configured"));
    return fetch("https://" + S.domain + "/api/2024-07/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": S.token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.errors && d.errors.length) throw new Error(d.errors[0].message);
        return d.data;
      });
  }

  /* Resolve the "Set Only" product for this pid and return its stock.
     Mirrors cart.js's discovery: explicit handle if configured, else search
     by set number, and never match the BUNDLE product for the single line. */
  function isBundleTitle(t) {
    return /led|light kit|lightkit|wall mount|wall-mount|display mount|bundle/i.test(String(t || ""));
  }

  function fetchLive(pid) {
    var prod = (window.BM_PRICING && window.BM_PRICING.products || {})[pid];
    if (!prod) return Promise.resolve({ qty: null });

    var full = prod.title || "";
    var num  = (full.match(/\((\w+)\)/) || [])[1];
    var handles = (window.BM_SHOPIFY && window.BM_SHOPIFY.handles || {})[pid] || {};
    var handle = typeof handles === "string" ? handles : handles.single;

    var VARIANTS = "variants(first:30){ nodes{ id title quantityAvailable availableForSale } }";

    var byHandle = handle
      ? sf("query($h:String!){ product(handle:$h){ title " + VARIANTS + " } }", { h: handle })
          .then(function (d) { return d.product; })
      : Promise.resolve(null);

    return byHandle
      .then(function (p) {
        if (p) return p;
        if (!num) return null;
        return sf(
          "query($q:String!){ products(first:10, query:$q){ nodes{ title handle " + VARIANTS + " } } }",
          { q: "title:" + num }
        ).then(function (d) {
          var nodes = (d.products && d.products.nodes) || [];
          for (var i = 0; i < nodes.length; i++) {
            if (!isBundleTitle(nodes[i].title)) return nodes[i];
          }
          return null;
        });
      })
      .then(function (p) {
        if (!p || !p.variants || !p.variants.nodes.length) return { qty: null };
        // Stock for the page = the most-available sellable variant. If Shopify
        // returns null (tracking off, or the token lacks the inventory scope)
        // we return null and the badge hides.
        var best = null;
        p.variants.nodes.forEach(function (v) {
          if (v.availableForSale === false) return;
          if (typeof v.quantityAvailable === "number") {
            best = best == null ? v.quantityAvailable : Math.max(best, v.quantityAvailable);
          }
        });
        return { qty: best };
      })
      .catch(function (e) {
        try { console.warn("[BM stock] live lookup failed for " + pid + ":", e.message); } catch (_e) {}
        return { qty: null };
      });
  }

  /* ------------------------------------------- provider: DEMO (pre-launch) */
  /* Deterministic per set — same shopper sees a consistent number across
     reloads, and two sets never show the same figure by coincidence.
     Replaced wholesale by fetchLive() once inventory is connected. */

  function seed(str) {                       // FNV-1a -> 32-bit int
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    return h;
  }
  function rng(s) {                          // mulberry32
    var a = s >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* Starting units. Fast movers (the expensive display pieces at the top of
     the catalog) sit lower; the cheaper long-tail sits higher. Always < 10. */
  function demoBase(pid, rand) {
    var pins = CFG.pin || {};
    if (pins[pid] != null) return pins[pid];              // falcon stays at 4

    var prod = (window.BM_PRICING && window.BM_PRICING.products || {})[pid];
    var price = prod && prod.plans && prod.plans.single ? prod.plans.single.once : 0;

    var lo, hi;
    if (price >= 350)      { lo = 3; hi = 4; }            // hero tier
    else if (price >= 250) { lo = 3; hi = 5; }
    else if (price >= 200) { lo = 4; hi = 6; }
    else                   { lo = 5; hi = 9; }            // long tail
    lo = Math.max(lo, FLOOR + 1);                         // room for at least one tick
    hi = Math.min(hi, CEIL);
    if (hi < lo) hi = lo;
    return lo + Math.floor(rand() * (hi - lo + 1));
  }

  /* How long until the next unit "sells".
     Paced off BM_STOCK_CONFIG.demoSteps (seconds), which front-loads the first
     drop so the behaviour is visible inside a normal session, then stretches
     out. Every wait carries ± demoJitter so no set ticks like a metronome and
     no two sets share a rhythm. The last value in the list repeats.
     LIVE MODE IGNORES ALL OF THIS — real orders set the pace. */
  var STEPS  = CFG.demoSteps && CFG.demoSteps.length ? CFG.demoSteps : [30, 180, 480, 1200];
  var JITTER = CFG.demoJitter != null ? CFG.demoJitter : 0.35;

  function demoStepMs(rand, idx) {
    var secs = STEPS[Math.min(idx, STEPS.length - 1)];
    var swing = 1 + (rand() * 2 - 1) * JITTER;            // e.g. 30s -> 20–41s
    return Math.max(5000, Math.round(secs * 1000 * swing));
  }

  function readStore() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch (e) { return {}; }
  }
  function writeStore(o) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch (e) {}
  }

  /* Ticks are counted per visit, so the fast first drop happens for whoever is
     actually looking at the page; the unit count itself persists across
     reloads, so it never springs back up to where it started. */
  var visitTicks = 0;

  function fetchDemo(pid) {
    var now = Date.now();
    var store = readStore();
    var rec = store[pid];
    var rand = rng(seed(pid) + visitTicks * 2654435761);

    if (!rec || typeof rec.qty !== "number") {
      rec = { qty: demoBase(pid, rand), next: now + demoStepMs(rand, 0) };
    } else {
      if (rec.next == null) rec.next = now + demoStepMs(rand, visitTicks);
      var guard = 0;
      while (rec.qty > FLOOR && now >= rec.next && guard++ < 50) {
        rec.qty -= 1;
        visitTicks++;
        rec.next = now + demoStepMs(rng(seed(pid) + visitTicks * 2654435761), visitTicks);
      }
      if (rec.qty <= FLOOR) { rec.qty = FLOOR; rec.next = now + 3600000; }   // stop at the floor
    }
    if (rec.qty < FLOOR) rec.qty = FLOOR;               // clamp older saved values
    if (rec.qty > CEIL)  rec.qty = CEIL;
    store[pid] = rec;
    writeStore(store);
    return Promise.resolve({ qty: rec.qty, next: rec.next });
  }

  /* --------------------------------------- provider: SUPPLIER (real-time) */
  /* Live units straight from your supplier's backend. This is the production
     path when inventory does not live in Shopify.

     CONFIGURE IN config.js:
       mode:     "supplier"
       endpoint: "https://your-bridge.example.com/stock"   (required)
       key:      "sku" | "handle" | "pid"      which identifier to send (default "sku")
       batch:    true  -> ONE call returns every item, cached for cacheMs
                 false -> one call per page, ?<key>=<value>
       cacheMs:  how long a response is reused (default 60000)
       headers:  optional extra request headers, e.g. an API key

     YOUR ENDPOINT MUST RETURN JSON. Any of these shapes is understood:
       single:  {"qty": 4}   {"quantity": 4}   {"available": 4}   {"stock": 4}
                {"inventory": 4}   {"quantityAvailable": 4}   or a bare  4
       batch:   {"7005192": 4, "7005313": 0, ...}
                {"items": [{"sku":"7005192","qty":4}, ...]}

     ANYTHING ELSE -- a non-200, a timeout, a malformed body, a missing key --
     resolves to null and the badge HIDES. It never falls back to a generated
     number, so a broken feed shows nothing rather than something untrue. */

  var SKU = {
    "falcon":"7005192","atat":"7005313","minas":"1001377","pokemon":"7002153","colosseum":"1000276",
    "barad-dur":"1000333","rivendell":"1000316","death-star":"7005419","sail-barge":"7005397",
    "star-destroyer":"7005252","porsche-gt3rs":"4002056","black-pearl":"1000365","koenigsegg":"4002232",
    "mclaren-p1":"4002172","red-bull-rb20":"4002206","ferrari-sf24":"4002207","porsche-911-rsr":"4002096",
    "gringotts":"7006417","enterprise-d":"1000356","mercedes-w14":"4002171","bugatti-chiron":"4002083",
    "lamborghini-sian":"4002115","ferrari-sp3":"4002143","titanic":"1000294","eiffel-tower":"1000307",
    "creel-house":"2001330","upside-down":"7005810","arkham-asylum":"7006052",
    "sanctum-sanctorum":"7006218","x-mansion":"7006294","avengers-tower":"7006269",
    "daily-bugle":"7006178","ewok-village":"1000236","razor-crest":"7005331",
    "dropship-atot":"7005388","porsche-911-targa":"1000295","venator":"7005367","jango-starship":"7005389"
  };

  function keyFor(pid) {
    var k = CFG.key || "sku";
    if (k === "pid") return pid;
    if (k === "handle") {
      var h = (window.BM_SHOPIFY && window.BM_SHOPIFY.handles || {})[pid];
      return typeof h === "string" ? h : (h && h.single) || pid;
    }
    return SKU[pid] || pid;
  }

  /* Pull a number out of whatever shape the endpoint returned. */
  function readQty(v) {
    if (typeof v === "number" && isFinite(v)) return v;
    if (!v || typeof v !== "object") return null;
    var fields = ["qty","quantity","available","stock","inventory","quantityAvailable","on_hand","onHand"];
    for (var i = 0; i < fields.length; i++) {
      var n = v[fields[i]];
      if (typeof n === "number" && isFinite(n)) return n;
      if (typeof n === "string" && n !== "" && isFinite(+n)) return +n;
    }
    return null;
  }

  var SUP_TIMEOUT_MS = CFG.timeoutMs != null ? CFG.timeoutMs : 8000;
  var supCache = null, supAt = 0, supInflight = null;

  function supFetch(url) {
    var ctl = (typeof AbortController === "function") ? new AbortController() : null;
    var to = setTimeout(function () { try { ctl && ctl.abort(); } catch (_e) {} }, SUP_TIMEOUT_MS);
    var opt = { headers: CFG.headers || {} };
    if (ctl) opt.signal = ctl.signal;
    return fetch(url, opt)
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (j) { clearTimeout(to); return j; })
      .catch(function (e) { clearTimeout(to); throw e; });
  }

  function fetchSupplier(pid) {
    var base = CFG.endpoint;
    if (!base) {
      try { console.error("[BM stock] mode 'supplier' but BM_STOCK_CONFIG.endpoint is empty — nothing rendered."); } catch (_e) {}
      return Promise.resolve({ qty: null });
    }
    var k = keyFor(pid);
    var ttl = CFG.cacheMs != null ? CFG.cacheMs : 60000;

    if (CFG.batch) {
      if (supCache && (Date.now() - supAt) < ttl) return Promise.resolve({ qty: pickBatch(supCache, k) });
      if (!supInflight) {
        supInflight = supFetch(base)
          .then(function (j) { supCache = j; supAt = Date.now(); supInflight = null; return j; })
          .catch(function (e) {
            supInflight = null;
            try { console.warn("[BM stock] supplier batch failed:", e.message); } catch (_e) {}
            return null;
          });
      }
      return supInflight.then(function (j) { return { qty: j ? pickBatch(j, k) : null }; });
    }

    var url = base + (base.indexOf("?") >= 0 ? "&" : "?") +
              encodeURIComponent(CFG.key || "sku") + "=" + encodeURIComponent(k);
    return supFetch(url)
      .then(function (j) { return { qty: readQty(j) }; })
      .catch(function (e) {
        try { console.warn("[BM stock] supplier lookup failed for " + pid + ":", e.message); } catch (_e) {}
        return { qty: null };
      });
  }

  function pickBatch(j, k) {
    if (!j || typeof j !== "object") return null;
    if (Object.prototype.hasOwnProperty.call(j, k)) return readQty(j[k]) != null ? readQty(j[k]) : readQty({ qty: j[k] });
    var list = j.items || j.stock || j.data;
    if (Object.prototype.toString.call(list) === "[object Array]") {
      for (var i = 0; i < list.length; i++) {
        var row = list[i] || {};
        if (String(row.sku || row.handle || row.pid || row.id) === String(k)) return readQty(row);
      }
    }
    return null;
  }

  /* ----------------------------------------- provider: MANUAL (real units) */
  /* FOR THE DEV TEAM: put real unit counts in BM_STOCK_CONFIG.counts in
     config.js, keyed by the page pid. A pid with no number renders no badge,
     so a set you have not counted yet never shows a figure. Nothing decays
     on a timer here — the number changes when you change it. */

  function fetchManual(pid) {
    var n = COUNTS[pid];
    return Promise.resolve({ qty: (typeof n === "number" && isFinite(n)) ? n : null });
  }

  /* ------------------------------------------- provider: LABEL (no number) */
  /* A standing low-stock notice with no unit count and no countdown. Nothing
     is generated and nothing ticks: it renders exactly the text in
     CFG.label, on the sets listed in CFG.labelSets (null = every set).
     Keep it on the sets where it is actually true and off the rest —
     labelSets is there so it does not have to be all-or-nothing. */

  var LABEL_CSS_ID = "bm-lowstock-blink";

  function injectLabelCSS() {
    try {
      if (document.getElementById(LABEL_CSS_ID)) return;
      var s = document.createElement("style");
      s.id = LABEL_CSS_ID;
      s.textContent =
        "@keyframes bmLowPulse{0%,100%{opacity:1}50%{opacity:.45}}" +
        ".lowstock.bm-label{border-color:#c0392b;background:#fdf2ef;color:#c0392b;" +
        "animation:bmLowPulse 1.25s ease-in-out infinite}" +
        ".lowstock.bm-label .bm-dot{animation:bmLowPulse .8s ease-in-out infinite}" +
        "@media (prefers-reduced-motion:reduce){" +
        ".lowstock.bm-label,.lowstock.bm-label .bm-dot{animation:none}}";
      (document.head || document.documentElement).appendChild(s);
    } catch (_e) {}
  }

  function paintLabel(on) {
    injectLabelCSS();
    var text = CFG.label || "LOW STOCK — SELLING OUT SOON";
    els().forEach(function (el) {
      if (!on) { el.style.display = "none"; return; }
      el.classList.add("bm-label");
      el.classList.remove("critical");
      el.textContent = "";
      var dot = document.createElement("span");
      dot.className = "bm-dot";
      dot.textContent = "●";
      var span = document.createElement("span");
      span.textContent = text;
      el.appendChild(dot);
      el.appendChild(span);
      el.setAttribute("role", "status");
      el.style.display = "";
      el.style.visibility = "visible";
    });
  }

  function labelAppliesTo(pid) {
    var list = CFG.labelSets;
    if (list == null) return true;                       // null -> every set
    if (Object.prototype.toString.call(list) === "[object Array]") {
      for (var i = 0; i < list.length; i++) if (list[i] === pid) return true;
      return false;
    }
    return !!list[pid];                                   // or an object map
  }

  /* ------------------------------------------------------------ controller */

  var timer = null;

  /* Which data source actually runs, after the environment gate. */
  function provider() {
    if (MODE === "live") return fetchLive;
    if (MODE === "supplier") return fetchSupplier;
    if (MODE === "manual") {
      var pid = (window.BM_PAGE || {}).pid;
      if (typeof COUNTS[pid] === "number") return fetchManual;
      return isPreviewHost() ? fetchDemo : fetchManual;   // preview: show the UI; prod: show nothing
    }
    /* "demo" — build aid only. Blocked on production hosts. */
    return isPreviewHost() ? fetchDemo : null;
  }

  function tick(pid) {
    var get = provider();
    return get(pid).then(function (res) {
      paint(res.qty);
      clearTimeout(timer);
      var wait = (provider() === fetchDemo)
        ? Math.max(1000, (res.next || Date.now() + REFRESH) - Date.now())
        : REFRESH;
      timer = setTimeout(function () { tick(pid); }, wait);
      return res;
    });
  }

  function init() {
    if (!els().length) return;
    var pid = (window.BM_PAGE && window.BM_PAGE.pid) || null;
    if (!pid) { paint(null); return; }

    if (provider() === fetchDemo) {
      try {
        console.warn(
          "[BM stock] PLACEHOLDER MODE on " + location.hostname + " — the number " +
          "in the low-stock badge is GENERATED, not real inventory. This runs on " +
          "development and preview hosts only; the production domain renders " +
          "BM_STOCK_CONFIG.counts or nothing. Dev team: real integration surface " +
          "is fetchLive() (Shopify quantityAvailable), or fill in counts{} in config.js."
        );
      } catch (e) {}
    }
    tick(pid);
    // repaint when the tab comes back, so a backgrounded page isn't stale
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden) tick(pid);
    });
  }

  /* public surface — handy for QA: BMStock.set(3) / BMStock.refresh() */
  window.BMStock = {
    mode: MODE,
    refresh: function () { return tick((window.BM_PAGE || {}).pid); },
    set: function (n) { paint(n); },          // preview any value without waiting
    fetchLive: fetchLive
  };

  /* mode "off" — render nothing and hide the container outright.
     NOTE: every branch below used `MODE === "live" ? fetchLive : fetchDemo`, so
     ANY value that wasn't "live" fell through to the placeholder generator —
     "off" silently behaved exactly like "demo". This is the explicit stop. */
  function hideAll() {
    try { els().forEach(function (el) { el.style.display = "none"; }); } catch (_e) {}
    try { console.info("[BM stock] mode \'off\' — hidden, no number rendered."); } catch (_e) {}
  }
  function boot() {
    if (MODE === "off") { hideAll(); return; }
    if (MODE === "label") {
      if (!els().length) return;
      paintLabel(labelAppliesTo((window.BM_PAGE || {}).pid));
      return;
    }
    if (provider() === null) {
      hideAll();
      try {
        console.error(
          "[BM stock] mode 'demo' is a BUILD AID and is blocked on " +
          location.hostname + " because the number it renders is invented. " +
          "Nothing was displayed. To light the badge up here, either set " +
          "BM_STOCK_CONFIG.counts = { \"<pid>\": <real units> } in config.js, " +
          "or switch to mode 'live' with Shopify inventory tracking on."
        );
      } catch (_e) {}
      return;
    }
    init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

/*bm-tail-patched*/
